import { Select } from '@base-ui/react';
import { useTranslation } from 'react-i18next';

import type { ColorTheme } from '~/api/user/types';

import { useCollisionPadding } from '~/hooks/useCollisionPadding';
import { brandColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';

import { circleStyle, halfStyles, itemsContainerStyle, itemStyle, popupStyle, rotatedStyle, wrapperStyle } from './ColorPicker.css';

const colorThemes = Object.keys(brandColorThemes) as ColorTheme[];

type Props = {
  onSelect: (colorTheme: ColorTheme) => void;
  value: ColorTheme;
};

export const ColorPicker = ({ onSelect, value }: Props) => {
  const collisionPadding = useCollisionPadding();

  const { t } = useTranslation(['common']);

  return (
    <Select.Root modal={false} onValueChange={(newValue) => newValue && onSelect(newValue)} value={value}>
      <div className={wrapperStyle}>
        <Select.Trigger
          aria-label={t('common:colorPicker.ariaLabel', { colorTheme: t(`common:colorPicker.options.${value}`) })}
          className={clsx(brandColorThemes[value], circleStyle)}
        >
          <ColorHalves />
        </Select.Trigger>
      </div>

      <Select.Portal>
        <Select.Positioner alignItemWithTrigger={false} collisionPadding={collisionPadding} sideOffset={8}>
          <Select.Popup className={popupStyle}>
            <Select.List className={itemsContainerStyle}>
              {colorThemes.map((colorTheme) => (
                <Select.Item
                  aria-label={t(`common:colorPicker.options.${colorTheme}`)}
                  className={itemStyle}
                  key={colorTheme}
                  value={colorTheme}
                >
                  <ColorPickerCircle colorTheme={colorTheme} />
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
};

const ColorHalves = () => (
  <div className={rotatedStyle}>
    <div className={halfStyles.top} />
    <div className={halfStyles.bottom} />
  </div>
);

const ColorPickerCircle = ({ colorTheme }: { colorTheme: ColorTheme }) => (
  <div className={clsx(brandColorThemes[colorTheme], circleStyle)}>
    <ColorHalves />
  </div>
);
